import React, { useState, useEffect } from 'react';
import axios from 'axios';

const PromoCodeManager = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    coinAmount: '',
    stock: '',
    expiresAt: '',
    isActive: true
  });
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'history'

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/promo-codes/admin', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(response.data);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      alert('Kodlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code || !formData.coinAmount) {
      alert('Kod ve puan miktarı gereklidir');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      
      if (editingCode) {
        await axios.put(
          `/api/promo-codes/admin/${editingCode.id}`,
          {
            ...formData,
            coinAmount: parseInt(formData.coinAmount),
            stock: formData.stock ? parseInt(formData.stock) : 0,
            expiresAt: formData.expiresAt || null
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        alert('Kod güncellendi!');
      } else {
        await axios.post(
          '/api/promo-codes/admin',
          {
            ...formData,
            coinAmount: parseInt(formData.coinAmount),
            stock: formData.stock ? parseInt(formData.stock) : 0,
            expiresAt: formData.expiresAt || null
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        alert('Kod oluşturuldu!');
      }

      setShowForm(false);
      setEditingCode(null);
      setFormData({
        code: '',
        coinAmount: '',
        stock: '',
        expiresAt: '',
        isActive: true
      });
      fetchCodes();
    } catch (error) {
      console.error('Error saving promo code:', error);
      alert(error.response?.data?.error || 'Kod kaydedilirken bir hata oluştu');
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      coinAmount: code.coinAmount || code.coin_amount,
      stock: code.stock || '',
      expiresAt: code.expiresAt || code.expires_at ? new Date(code.expiresAt || code.expires_at).toISOString().slice(0, 16) : '',
      isActive: code.isActive !== undefined ? code.isActive : code.is_active !== 0
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kodu silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/promo-codes/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Kod silindi!');
      fetchCodes();
    } catch (error) {
      console.error('Error deleting promo code:', error);
      alert('Kod silinirken bir hata oluştu');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR');
  };

  const isExpired = (code) => {
    if (!code.expiresAt && !code.expires_at) return false;
    const expiresAt = code.expiresAt || code.expires_at;
    return new Date(expiresAt) < new Date();
  };

  const isOutOfStock = (code) => {
    const stock = code.stock || 0;
    const usedCount = code.usedCount || code.used_count || 0;
    return stock > 0 && usedCount >= stock;
  };

  const activeCodes = codes.filter(code => {
    const active = code.isActive !== undefined ? code.isActive : code.is_active !== 0;
    return active && !isExpired(code) && !isOutOfStock(code);
  });

  const historyCodes = codes.filter(code => {
    const active = code.isActive !== undefined ? code.isActive : code.is_active !== 0;
    return !active || isExpired(code) || isOutOfStock(code);
  });

  return (
    <div className="bg-gradient-card rounded-xl p-6 border border-gold/30">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gold">Puan Kodu Yönetimi</h2>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingCode(null);
            setFormData({
              code: '',
              coinAmount: '',
              stock: '',
              expiresAt: '',
              isActive: true
            });
          }}
          className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-4 py-2 rounded-lg transition-all duration-300 shadow-gold"
        >
          {showForm ? 'İptal' : '+ Yeni Kod Oluştur'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-navy-800 rounded-lg border border-gold/20">
          <h3 className="text-xl font-bold text-gold mb-4">
            {editingCode ? 'Kodu Düzenle' : 'Yeni Kod Oluştur'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white mb-2">Kod *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="ÖRN: WELCOME2024"
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gold"
                required
                disabled={!!editingCode}
              />
            </div>

            <div>
              <label className="block text-white mb-2">Puan Miktarı *</label>
              <input
                type="number"
                value={formData.coinAmount}
                onChange={(e) => setFormData({ ...formData, coinAmount: e.target.value })}
                placeholder="100"
                min="1"
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gold"
                required
              />
            </div>

            <div>
              <label className="block text-white mb-2">Stok Sayısı (0 = sınırsız)</label>
              <input
                type="number"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                placeholder="100"
                min="0"
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gold"
              />
            </div>

            <div>
              <label className="block text-white mb-2">Bitiş Tarihi</label>
              <input
                type="datetime-local"
                value={formData.expiresAt}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                className="w-full px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-gold bg-navy-700 border-gold/30 rounded focus:ring-gold"
                />
                Aktif
              </label>
            </div>
          </div>

          <div className="mt-4 flex gap-4">
            <button
              type="submit"
              className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-6 py-2 rounded-lg transition-all duration-300 shadow-gold"
            >
              {editingCode ? 'Güncelle' : 'Oluştur'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingCode(null);
              }}
              className="bg-navy-700 hover:bg-navy-600 text-white px-6 py-2 rounded-lg transition-colors"
            >
              İptal
            </button>
          </div>
        </form>
      )}

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gold/20">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'active'
              ? 'text-gold border-b-2 border-gold'
              : 'text-white/60 hover:text-gold'
          }`}
        >
          Aktif Kodlar ({activeCodes.length})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'history'
              ? 'text-gold border-b-2 border-gold'
              : 'text-white/60 hover:text-gold'
          }`}
        >
          Geçmiş ({historyCodes.length})
        </button>
      </div>

      {/* Codes List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold mx-auto"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gold/20">
                <th className="text-left py-3 px-4 text-gold font-semibold">Kod</th>
                <th className="text-left py-3 px-4 text-gold font-semibold">Puan</th>
                <th className="text-left py-3 px-4 text-gold font-semibold">Stok</th>
                <th className="text-left py-3 px-4 text-gold font-semibold">Kullanım</th>
                <th className="text-left py-3 px-4 text-gold font-semibold">Bitiş Tarihi</th>
                <th className="text-left py-3 px-4 text-gold font-semibold">Durum</th>
                <th className="text-left py-3 px-4 text-gold font-semibold">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {(activeTab === 'active' ? activeCodes : historyCodes).map((code) => {
                const usages = code.usages || [];
                const stock = code.stock || 0;
                const usedCount = code.usedCount || code.used_count || 0;
                const expired = isExpired(code);
                const outOfStock = isOutOfStock(code);
                const active = code.isActive !== undefined ? code.isActive : code.is_active !== 0;

                return (
                  <tr key={code.id} className="border-b border-gold/10 hover:bg-navy-800/50">
                    <td className="py-3 px-4 text-white font-mono font-bold">{code.code}</td>
                    <td className="py-3 px-4 text-gold font-semibold">{code.coinAmount || code.coin_amount}</td>
                    <td className="py-3 px-4 text-white">
                      {stock === 0 ? 'Sınırsız' : `${stock - usedCount} / ${stock}`}
                    </td>
                    <td className="py-3 px-4 text-white">{usedCount}</td>
                    <td className="py-3 px-4 text-white">{formatDate(code.expiresAt || code.expires_at)}</td>
                    <td className="py-3 px-4">
                      {!active ? (
                        <span className="px-2 py-1 bg-gray-600 text-white rounded text-xs">Pasif</span>
                      ) : expired ? (
                        <span className="px-2 py-1 bg-red-600 text-white rounded text-xs">Süresi Dolmuş</span>
                      ) : outOfStock ? (
                        <span className="px-2 py-1 bg-orange-600 text-white rounded text-xs">Stok Tükendi</span>
                      ) : (
                        <span className="px-2 py-1 bg-green-600 text-white rounded text-xs">Aktif</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(code)}
                          className="text-gold hover:text-yellow-400 transition-colors"
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(code.id)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          title="Sil"
                        >
                          🗑️
                        </button>
                        {usages.length > 0 && (
                          <button
                            onClick={() => {
                              const usageList = usages.map(u => {
                                const user = u.user || {};
                                return `• ${user.username || 'Bilinmeyen'} - ${formatDate(u.createdAt || u.created_at)}`;
                              }).join('\n');
                              alert(`Kullanımlar:\n\n${usageList}`);
                            }}
                            className="text-blue-400 hover:text-blue-300 transition-colors"
                            title="Kullanımları Gör"
                          >
                            👥
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {(activeTab === 'active' ? activeCodes : historyCodes).length === 0 && (
            <div className="text-center py-8 text-white/60">
              {activeTab === 'active' ? 'Aktif kod bulunmamaktadır' : 'Geçmiş kod bulunmamaktadır'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromoCodeManager;
